import { FC, useEffect } from 'react';
import { CustomerServiceFilled } from '@ixigo/icons';
import GroupBookingForm from '../components/GroupBookingForm';
import GroupGlobe from '../images/Group_globe.svg';
import {
    GROUP_FEATURE_CHIPS,
    GROUP_BOOKING_META_TAGS,
    GROUP_BOOKING_PAGE_TITLE,
    GROUP_BOOKING_SUBHEADING,
    GROUP_BOOKING_WHY_IXIGO_HEADING,
    GROUP_BOOKING_LOGO_URL,
    GROUP_BOOKING_HEADING_ARRAY,
    GROUP_BOOKING_SUBHEADING_ARRAY,
    GROUP_BOOKING_WHY_IXIGO_CONTENT,
} from '../constants/group';
import HeaderNavigation from '../components/Navigation/Header';
import { getCookieHelpers } from '../utils/cookie';
import { Client } from '../enums/common';

function GroupChip({ icon, label, text }: { icon: string; label: string; text: string }) {
    return (
        <div
            className='lg:w-[400px] flex flex-col items-start gap-2 rounded-[40px]'
            style={{
                padding: '20px 30px 30px',
                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            }}
        >
            <img src={icon} alt={label} className='w-50 h-50' />
            <h3 className='text-lg font-semibold'>{label}</h3>
            <p className='text-charcoal-500'>{text}</p>
        </div>
    );
}

const GroupBooking: FC = () => {
    const containerClass =
        ' max-w-desktop-max mx-auto px-4 sm:px-8 lg:px-16 xl:px-20 2xl:px-24 max-sm:max-w-[640px] max-md:max-w-[768px] max-lg:max-w-[768px] max-xl:max-w-[1024px] max-2xl:max-w-[1280px] ';
    const clientId = getCookieHelpers().getCookie('clientId');
    useEffect(() => {
        const createdElements: HTMLElement[] = [];

        GROUP_BOOKING_META_TAGS.forEach((ele) => {
            const meta = document.createElement('meta');
            Object.keys(ele).forEach((key: string) => {
                const value = ele[key as keyof typeof ele];
                if (value) {
                    meta.setAttribute(key, value);
                }
            });
            document.getElementsByTagName('head')[0].appendChild(meta);
            createdElements.push(meta);
        });

        const originalTitle = document.title;
        document.title = GROUP_BOOKING_PAGE_TITLE;

        return () => {
            createdElements.forEach((element) => {
                element.remove();
            });
            document.title = originalTitle;
        };
    }, []);

    return (
        <main className='mb-80 relative'>
            <div className='lg:sticky top-0 z-10'>
                {clientId === Client.IXIWEB && <HeaderNavigation />}
                {clientId === Client.IXIMWEB && (
                    <div className={`justify-between items-center mt-20 mb-40 flex sm:hidden ${containerClass}`}>
                        <img
                            src={GROUP_BOOKING_LOGO_URL}
                            alt='ixigo.com'
                            title='ixigo.com'
                            id='ixiLogoImg'
                            className='w-[60px] h-[32px] md:w-[88px] md:h-[39px]'
                        />
                        <a href='/support' className='flex items-center gap-5 text-[#5a5f73] outline-none'>
                            <CustomerServiceFilled />
                            <span className='lg:text-base md:text-xs'>Customer Service</span>
                        </a>
                    </div>
                )}
            </div>
            <div className='absolute inset-0 flex justify-center md:hidden lg:block pointer-events-none -z-10'>
                <div
                    className='max-w-desktop-max mx-auto h-full bg-no-repeat mt-40'
                    style={{
                        backgroundImage: `url(${GroupGlobe})`,
                        backgroundSize: '94%',
                        backgroundPosition: 'center 52%',
                        WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 40%, transparent 60%)',
                        maskImage: 'linear-gradient(to bottom, black 0%, black 40%, transparent 60%)',
                    }}
                />
            </div>
            <div className='pt-10 pb-40 lg:mt-40 lg:pt-40'>
                <div className={`${containerClass}`}>
                    <div className='flex flex-col lg:flex-row justify-center lg:justify-between lg:gap-80'>
                        <div className='lg:max-w-[650px] flex-1 lg:mt-30 lg:text-charcoal-800 mb-20 lg:mb-0 rounded-md'>
                            <div>
                                <h2 className='mb-10 lg:text-[44px] text-[22px] font-bold leading-tight text-center md:text-left'>
                                    {GROUP_BOOKING_HEADING_ARRAY[0]}
                                    <span className='text-[#FC790D] pl-2'>{GROUP_BOOKING_HEADING_ARRAY[1]}</span>{' '}
                                    {GROUP_BOOKING_HEADING_ARRAY[2]}
                                </h2>
                                <div className='flex flex-row items-center justify-center md:justify-start md:mx-10 gap-3 lg:gap-7 text-[12px] md:text-sm lg:text-base font-normal text-charcoal-500 mx:10'>
                                    {GROUP_BOOKING_SUBHEADING_ARRAY.map((ele: string) => (
                                        <span key={ele}>• {ele}</span>
                                    ))}
                                </div>
                                <p className='hidden lg:block text-lg text-charcoal-500 pt-10 tracking-normal font-light'>
                                    {GROUP_BOOKING_SUBHEADING}
                                </p>
                            </div>
                        </div>
                        <GroupBookingForm />
                    </div>
                </div>
            </div>
            <div className={`${containerClass} flex flex-col gap-30 lg:mt-40`}>
                <div className='flex flex-col gap-5 lg:gap-10 justify-center items-center text-center'>
                    <div className='text-[24px] lg:text-3xl font-bold'>{GROUP_BOOKING_WHY_IXIGO_HEADING}</div>
                    <span className='text-[#5E616E] tracking-wide text-[14px] lg:text-lg'>
                        {GROUP_BOOKING_WHY_IXIGO_CONTENT}
                    </span>
                </div>
                <div className='flex flex-col items-center justify-center lg:flex-row w-full gap-20 mb-80'>
                    {GROUP_FEATURE_CHIPS.map((chip) => (
                        <GroupChip key={chip.label} icon={chip.icon} label={chip.label} text={chip.text} />
                    ))}
                </div>
            </div>
        </main>
    );
};

export default GroupBooking;
